import { EApplicantType } from '@if/core/types'
import ReportIcon from '@mui/icons-material/Report'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Chip, IconButton, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import { FraudAnalysisResult } from '@src/data/types/FraudAnalysisResult'
import { useTranslation } from 'react-i18next'

interface FraudFlagsHeaderProps {
  creditApplicationId: string
  financingProgramId: string | null
  totalFlags: number
  activeFlags: number
  fraudAnalysis: FraudAnalysisResult | undefined | null
  tabApplicantValue: EApplicantType,
  coApplicantDisabled: boolean
  handleOpenFraudReport: () => void
  handleTabApplicantChange: (_event: React.SyntheticEvent, newValue: EApplicantType) => void
}

export const FraudFlagsHeader: React.FC<FraudFlagsHeaderProps> = ({
  creditApplicationId,
  financingProgramId,
  totalFlags,
  activeFlags,
  fraudAnalysis,
  tabApplicantValue,
  coApplicantDisabled,
  handleOpenFraudReport,
  handleTabApplicantChange
}) => {
  const { t } = useTranslation()
  const applicantTypeLabel = t(`common.${(tabApplicantValue === EApplicantType.Applicant ? 'applicant' : 'coApplicant')}`)

  const ipqsAnalysis = tabApplicantValue === EApplicantType.CoApplicant
    ? fraudAnalysis?.coapplicantIPQSAnalysis
    : fraudAnalysis?.applicantIPQSAnalysis

  const getFraudReportButtonColor = () => {
    if (!ipqsAnalysis) return 'inherit'
    const maxScore = Math.max(ipqsAnalysis.emailFraudScore || 0, ipqsAnalysis.phoneFraudScore || 0)
    return maxScore >= 85 ? 'error' : 'success'
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom>
            <WarningAmberIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('fraudFlags.title')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('fraudFlags.creditApplication')}: {creditApplicationId}
          </Typography>
          {financingProgramId && (
            <Typography variant="body2" color="text.secondary">
              {t('fraudFlags.program')}: {financingProgramId}
            </Typography>
          )}
          <Tabs value={tabApplicantValue} onChange={handleTabApplicantChange}>
            <Tab
              value={EApplicantType.Applicant}
              label={t('common.applicant')}
            />
            <Tab
              value={EApplicantType.CoApplicant}
              label={t('common.coApplicant')}
              disabled={coApplicantDisabled}
            />
          </Tabs>
        </Box>

        <Stack direction="row" spacing={2}>
          {ipqsAnalysis && (
            <IconButton
              onClick={handleOpenFraudReport}
              title={`${applicantTypeLabel} : ${t('fraudReport.viewReport')}`}
              disabled={!ipqsAnalysis}
            >
              <ReportIcon fontSize="medium" color={getFraudReportButtonColor()} />
            </IconButton>
          )}
          <Chip
            label={t('fraudFlags.activeFlags', { count: activeFlags })}
            color={activeFlags > 0 ? 'error' : 'default'}
          />
          <Chip label={t('fraudFlags.totalFlags', { count: totalFlags })} />
        </Stack>
      </Stack>
    </Paper>
  )
}
